/**
 * `/login` route — platform staff sign-in.
 *
 * `validateSearch` parses + types the URL search params via Zod so the
 * component receives `redirect?: string` + `reason?: "session_revoked"`
 * already validated (TanStack Router will coerce any invalid param to
 * undefined). The redirect param is re-run through `safeRedirectPath`
 * inside PlatformLoginPage before navigation (belt-and-suspenders D-06).
 *
 * Deferred: linkco ops manage platform staff credentials out-of-band — no
 * self-serve password-reset routes exist for link-admin (UI-SPEC §2).
 */
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { z } from "zod";
import { PlatformLoginPage } from "../features/auth/PlatformLoginPage";

const searchSchema = z.object({
  redirect: z.string().optional(),
  reason: z.enum(["session_revoked"]).optional(),
});

export const Route = createFileRoute("/login")({
  validateSearch: searchSchema,
  component: LoginRouteComponent,
});

function LoginRouteComponent(): React.JSX.Element {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  return (
    <PlatformLoginPage
      redirect={search.redirect}
      reason={search.reason}
      onSuccess={(to) => {
        // Restore the captured router-internal target via `navigate({ to })`.
        // Link Admin has no Vite base, so there is no basepath re-prepend to
        // worry about here. Parse the raw query string through the router's OWN
        // `parseSearch` (not a hand-rolled { key: string } object) so values
        // round-trip through `stringifySearch` verbatim instead of JSON-quoting
        // a string ("1" → "%221%22"). Keeps parity with hr-admin / employee-web.
        const qIdx = to.indexOf("?");
        const path = qIdx === -1 ? to : to.slice(0, qIdx);
        const rawQuery = qIdx === -1 ? "" : to.slice(qIdx);
        const parsedSearch = rawQuery.length > 0 ? router.options.parseSearch(rawQuery) : undefined;
        void navigate({
          to: path,
          ...(parsedSearch && Object.keys(parsedSearch).length > 0 ? { search: parsedSearch } : {}),
        });
      }}
    />
  );
}
